/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V48 - Final Search Fix: Part 1) ---
 * - MISSION: To upgrade the CarouselContext to facilitate a two-way
 * handshake, allowing carousels to report when they are initialized and
 * ready to receive commands.
 *
 * - THE FIX (Readiness State):
 * 1.  NEW STATE: A new `carouselReadyState` object is added. This state
 * will track the readiness of each individual carousel by its unique ID
 * (e.g., { 'featured-films': true, 'founding-filmmakers': false }).
 * 2.  NEW FUNCTION: A `setCarouselReady` function is added. This allows
 * child components (like `FeaturedFilms.jsx`) to update the context,
 * signaling that their internal Swiper instance has been successfully
 * initialized.
 *
 * - RESULT: This is the foundational change for the definitive fix. The
 * context now acts as a central hub for readiness status, enabling the
 * navigation logic in `Home.jsx` to wait until a carousel is fully
 * interactive before attempting to control it.
 */
import { useState, useCallback } from 'react';
import { CarouselContext } from './CarouselContext'; // Import the context

const CarouselProvider = ({ children }) => {
  const [targetSlide, setTargetSlide] = useState(null);
  // --- NEW: State to track the readiness of individual carousels ---
  const [carouselReadyState, setCarouselReadyState] = useState({});

  // --- NEW: Function for carousels to report their readiness ---
  const setCarouselReady = useCallback((carouselId, isReady) => {
    setCarouselReadyState(prevState => ({
      ...prevState,
      [carouselId]: isReady,
    }));
  }, []);

  const value = {
    targetSlide,
    setTargetSlide,
    carouselReadyState,
    setCarouselReady,
  };

  return (
    <CarouselContext.Provider value={value}>
      {children}
    </CarouselContext.Provider>
  );
};

export default CarouselProvider;
